import { ArrowUpRight, LoaderCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { REPORT_PRICE, SAMPLE_DOMAIN, SAMPLE_REPORT_URL } from "./landingData";

interface Props {
  open: boolean;
  onClose: () => void;
  onOrder: () => void;
}

export function SampleReportOverlay({ open, onClose, onOrder }: Props) {
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setLoaded(false);
  }, [open]);

  if (!open) return null;

  return (
    <div className="lp-sample-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="lp-sample" role="dialog" aria-modal="true" aria-labelledby="lp-sample-title">
        <header className="lp-sample-bar">
          <div className="lp-sample-title">
            <span className="lp-live-dot" aria-hidden="true" />
            <div>
              <strong id="lp-sample-title">Sample report</strong>
              <small>A full report for {SAMPLE_DOMAIN}, a demo website. Click around, it is fully interactive.</small>
            </div>
          </div>
          <div className="lp-sample-actions">
            <a className="lp-text-link" href={SAMPLE_REPORT_URL} target="_blank" rel="noreferrer">Open in new tab <ArrowUpRight size={14} /></a>
            <button className="button button-primary" type="button" onClick={onOrder}>Get my report, {REPORT_PRICE}</button>
            <button ref={closeRef} className="lp-icon-button" type="button" onClick={onClose} aria-label="Close sample report"><X size={18} /></button>
          </div>
        </header>
        <div className="lp-sample-frame">
          {!loaded && <div className="lp-sample-loading"><LoaderCircle className="spin" size={22} /><span>Loading sample report</span></div>}
          <iframe
            src={SAMPLE_REPORT_URL}
            title={`Sample website report for ${SAMPLE_DOMAIN}`}
            allow="clipboard-write"
            onLoad={() => setLoaded(true)}
            data-loaded={loaded || undefined}
          />
        </div>
      </div>
    </div>
  );
}
